import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { Dossier, DossierStats } from '../../types';
import { dossierApi } from '../../api/dossiers';

interface DossierState {
  dossiers: Dossier[];
  currentDossier: Dossier | null;
  stats: DossierStats | null;
  loading: boolean;
  error: string | null;
  totalCount: number;
  currentPage: number;
}

const initialState: DossierState = {
  dossiers: [],
  currentDossier: null,
  stats: null,
  loading: false,
  error: null,
  totalCount: 0,
  currentPage: 1,
};

export const fetchDossiers = createAsyncThunk(
  'dossiers/fetchDossiers',
  async (params?: {
    page?: number;
    conservation?: string;
    etat?: string;
    gestionnaire?: number;
  }) => {
    const response = await dossierApi.getAll(params);
    return response;
  }
);

export const fetchDossierById = createAsyncThunk(
  'dossiers/fetchDossierById',
  async (id: number) => {
    const response = await dossierApi.getById(id);
    return response;
  }
);

export const createDossier = createAsyncThunk(
  'dossiers/createDossier',
  async (data: Partial<Dossier>) => {
    const response = await dossierApi.create(data);
    return response;
  }
);

export const updateDossier = createAsyncThunk(
  'dossiers/updateDossier',
  async ({ id, data }: { id: number; data: Partial<Dossier> }) => {
    const response = await dossierApi.update(id, data);
    return response;
  }
);

export const deleteDossier = createAsyncThunk(
  'dossiers/deleteDossier',
  async (id: number) => {
    await dossierApi.delete(id);
    return id;
  }
);

export const fetchDossierStats = createAsyncThunk(
  'dossiers/fetchStats',
  async () => {
    const response = await dossierApi.getStats();
    return response;
  }
);

const dossierSlice = createSlice({
  name: 'dossiers',
  initialState,
  reducers: {
    clearCurrentDossier: (state) => {
      state.currentDossier = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch dossiers
      .addCase(fetchDossiers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDossiers.fulfilled, (state, action) => {
        state.loading = false;
        state.dossiers = action.payload.results;
        state.totalCount = action.payload.count;
      })
      .addCase(fetchDossiers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch dossiers';
      })
      // Fetch single dossier
      .addCase(fetchDossierById.fulfilled, (state, action) => {
        state.currentDossier = action.payload;
      })
      // Create dossier
      .addCase(createDossier.fulfilled, (state, action) => {
        state.dossiers.unshift(action.payload);
      })
      // Update dossier
      .addCase(updateDossier.fulfilled, (state, action) => {
        const index = state.dossiers.findIndex(d => d.id === action.payload.id);
        if (index !== -1) {
          state.dossiers[index] = action.payload;
        }
        if (state.currentDossier?.id === action.payload.id) {
          state.currentDossier = action.payload;
        }
      })
      // Delete dossier
      .addCase(deleteDossier.fulfilled, (state, action) => {
        state.dossiers = state.dossiers.filter(d => d.id !== action.payload);
      })
      // Fetch stats
      .addCase(fetchDossierStats.fulfilled, (state, action) => {
        state.stats = action.payload;
      });
  },
});

export const { clearCurrentDossier, clearError } = dossierSlice.actions;
export default dossierSlice.reducer;